import { Button, Drawer, Loading, Title } from "@components";
import styles from "@styles/pages/reports/reports.module.scss";
import TransitionsIdentificationCategories from "./TransitionsIdentificationCategories";
import RFVProfiles from "./RFVProfiles";
import RFVProfilesDetails from "./RFVProfilesDetails";
import { useEffect, useRef, useState } from "react";
import { MonthYearChart } from "pages/Reports/MonthYearChart";
import ContextSearch from "shared/containers/oraculo/ContextSearch";
import { ReportsAPI } from "providers/ReportsAPI";
import { If } from "shared/components/control-statements";
import { Numbers } from "shared/utils/Numbers";
import dayjs from "dayjs";
import { months } from "pages/Reports/Reports";

const Trend = ({ growth }) => {
  const isNegative = growth < 0;
  const iconClass = isNegative
    ? "fas fa-arrow-trend-down"
    : "far fa-arrow-trend-up";
  const iconColor = isNegative ? "#e48186" : "#51D9A3";
  const sign = growth > 0 ? "+" : "";
  return (
    <>
      {sign}
      {Numbers.number(growth, 2)}%{" "}
      <i className={iconClass} style={{ color: iconColor }} />
    </>
  );
};

const BigNumberTitles = {
  total_customers: "Total de clientes com perfil",
  best_transition: "Migraram para perfil melhor",
  worst_transition: "Migraram para perfil pior",
  no_transition: "Permaneceram no mesmo perfil",
};

const BigNumberTooltips = {
  total_customers:
    "Quantidade de clientes com ao menos 3 pedidos e mapeados pelo RFV.",
  best_transition: "Quantidade de clientes que migraram para um perfil melhor.",
  worst_transition: "Quantidade de clientes que migraram para um perfil pior.",
  no_transition: "Quantidade de clientes que permaneceram no mesmo perfil.",
};

const getCurrentMonthYear = () => {
  const today = new Date();

  const date = new Date(today.getFullYear(), today.getMonth());
  const mes = months[date.getMonth()];
  const ano = date.getFullYear();

  return {
    name: `${mes} ${ano}`,
    id: `${date.getMonth() + 1}`,
  };
};

const BigNumber = ({ data, onClick }) => {
  return (
    <div className={styles.bigNumber}>
      <span className={styles.month}>{getCurrentMonthYear().name}</span>
      <div className="d-flex flex-column">
        <div className="d-flex align-center">
          <p className={styles.number}>{data.value.toLocaleString("pt-BR")}</p>
        </div>
        <div className={styles.title}>
          <p>{BigNumberTitles[data.alias]}</p>
          <span className={styles.info} title={BigNumberTooltips[data.alias]}>
            <i className="fal fa-info-circle" />
          </span>
        </div>
      </div>
      <Button design="default-icon-mini-2" onClick={onClick}>
        <i className="far fa-chart-waterfall" />
      </Button>
    </div>
  );
};

const BigNumberFilled = ({ data, onClick }) => {
  return (
    <div className={styles.bigNumberFilled}>
      <div className="d-flex flex-column z-index-1">
        <div className="d-flex align-center">
          <p className={styles.number}>{data?.value.toLocaleString("pt-BR")}</p>
          <div className={styles.growth}>
            <Trend growth={data?.growth} />
          </div>
        </div>
        <div className={styles.title}>
          <p>{BigNumberTitles["total_customers"]}</p>
          <span
            className={styles.info}
            title={BigNumberTooltips["total_customers"]}
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
      </div>
      <Button design="default-icon-mini-2" onClick={onClick}>
        <i className="far fa-chart-waterfall" />
      </Button>
    </div>
  );
};

export default function Index() {
  const [selectedData, setSelectedData] = useState(null);
  const [openContextSearch, setOpenContextSearch] = useState(false);
  const [context, setContext] = useState(null);
  const [loadingHistoric, setLoadingHistoric] = useState(false);

  const numbers = (stats) => {
    return [
      {
        alias: "best_transition",
        value: stats?.best_transition?.value || 0,
      },
      {
        alias: "worst_transition",
        value: stats?.worst_transition?.value || 0,
      },
      {
        alias: "no_transition",
        value: stats?.no_transition?.value || 0,
      },
    ];
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    const { brand, unit } = context || {};

    setLoading(true);
    const provider = new ReportsAPI();
    const response = await provider.rfv({
      brand_id: brand?.id,
      unit_id: unit?.id,
    });

    setData(response);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [context]);

  const fetchHistory = async (payload) => {
    setLoadingHistoric(true);

    const { year, month } = payload;
    const { brand, unit } = context || {};
    const provider = new ReportsAPI();

    const results = await provider.rfvHistory(
      "keynumbers",
      year,
      month,
      brand?.id,
      unit?.id
    );

    const limit = month ? dayjs(`${year}-${month}`).endOf("month").date() : 12;
    const rows = Array(limit).fill(null);

    if (!results || !results.length) {
      setLoadingHistoric(false);
      return rows;
    }

    for (const result of results) {
      const parsedDate = month
        ? dayjs(result.date, "DD/MM/YYYY")
        : dayjs(result.date, "MM/YYYY");

      const index = month ? parsedDate.date() : parsedDate.month() + 1;

      if (index >= 1 && index <= limit) {
        const data = result[selectedData.alias];

        rows[index - 1] = {
          value: data?.value ?? 0,
          growth: data?.growth ?? 0,
        };
      }
    }

    setLoadingHistoric(false);

    return rows;
  };

  return (
    <>
      <Title
        title="Perfis RFV"
        backTo={{ url: "/relatorios/clientes", name: "Clientes" }}
      >
        {context && (
          <>
            <div className={styles.tagFilter}>
              <If cond={context.type === "BRAND"}>
                <span>Marca - {context?.brand?.name}</span>
              </If>
              <If cond={context.type === "UNIT"}>
                <span>
                  Unidade - {context?.brand?.name} / {context?.unit?.name}
                </span>
              </If>
              <div className={styles.remove} onClick={() => setContext(null)}>
                <i className="fal fa-times" />
              </div>
            </div>
          </>
        )}
        <Button onClick={() => setOpenContextSearch(true)}>Isolar base</Button>
      </Title>

      <If cond={loading}>
        <Loading fullContent />
      </If>

      <If cond={!loading}>
        <div className={styles.bigNumbersRFV}>
          <BigNumberFilled
            data={data?.statistics?.total_customers}
            onClick={() =>
              setSelectedData({
                name: BigNumberTitles["total_customers"],
                alias: "total_customers",
              })
            }
          />
          {numbers(data?.statistics || {}).map((item) => {
            return (
              <BigNumber
                data={item}
                key={item.alias}
                onClick={() => {
                  setSelectedData({
                    name: BigNumberTitles[item.alias],
                    alias: item.alias,
                  });
                }}
              />
            );
          })}
        </div>

        <RFVProfiles data={data} context={context} />
        <RFVProfilesDetails context={context} />
        <TransitionsIdentificationCategories context={context} />
      </If>

      <Drawer
        visible={selectedData}
        onClose={() => setSelectedData(null)}
        withoutTransition
        direction="full-center"
        showCloseButton={false}
      >
        <MonthYearChart
          selectedData={selectedData}
          enableMonth={false}
          onFetchData={fetchHistory}
          loading={loadingHistoric}
          onClose={() => setSelectedData(false)}
          startOn2025
          invertColorSign={selectedData?.alias === "worst_transition"}
        />
      </Drawer>

      <ContextSearch
        visible={openContextSearch}
        onChangeContext={(context) => {
          setOpenContextSearch(false);
          const updateContext = ({ type, brand, unit, multi_brand }) => {
            setContext({
              type,
              brand,
              unit,
              multi_brand,
            });
          };

          updateContext(context);
        }}
        onClose={() => setOpenContextSearch(false)}
      />
    </>
  );
}
